import React from 'react';
import { MapPin, Phone, Mail, Twitter, Linkedin, Facebook, Clock, FileText, Shield, Award } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import enausaLogo from "@/assets/enausa-official-logo.png";

const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="bg-enausa-navy text-enausa-text-on-navy">
      {/* Main Footer Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Company Information */}
            <div>
              <div className="mb-6">
                <img 
                  src={enausaLogo} 
                  alt="ENAUSA - Impulsando tu energía" 
                  className="h-16 w-auto mb-4 brightness-0 invert"
                  loading="lazy"
                  decoding="async"
                  width="137"
                  height="64"
                />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('footer.description')}
              </p>
              
              {/* Social Media */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">{t('footer.followUs')}</h4>
                <div className="flex space-x-3">
                  <a href="#" className="p-2 rounded-full bg-enausa-navy-light hover:bg-white hover:text-enausa-navy transition-all duration-300 group">
                    <Twitter className="w-4 h-4 text-gray-300 group-hover:text-enausa-navy" />
                  </a>
                  <a href="#" className="p-2 rounded-full bg-enausa-navy-light hover:bg-white hover:text-enausa-navy transition-all duration-300 group">
                    <Facebook className="w-4 h-4 text-gray-300 group-hover:text-enausa-navy" />
                  </a>
                  <a href="#" className="p-2 rounded-full bg-enausa-navy-light hover:bg-white hover:text-enausa-navy transition-all duration-300 group">
                    <Linkedin className="w-4 h-4 text-gray-300 group-hover:text-enausa-navy" />
                  </a>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-xl font-semibold text-white mb-6">{t('footer.services')}</h4>
              <ul className="space-y-3 text-gray-300">
                <li>
                  <a href="#servicios" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('services.infrastructure.title')}
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('services.installation.title')}
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('services.maintenance.title')}
                  </a>
                </li>
                <li>
                  <a href="#servicios" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('services.permits.title')}
                  </a>
                </li>
              </ul>
              
              {/* Certifications */}
              <div className="mt-6">
                <h5 className="text-lg font-semibold text-white mb-3">{t('footer.certifications')}</h5>
                <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex items-center">
                      <Shield className="w-3 h-3 mr-2 text-white" />
                      <span>{t('footer.qualityStandards')}</span>
                    </div>
                  <div className="flex items-center">
                    <Award className="w-3 h-3 mr-2 text-white" />
                    <span>{t('footer.laborSafety')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-xl font-semibold text-white mb-6">{t('footer.company')}</h4>
              <ul className="space-y-3 text-gray-300">
                <li>
                  <a href="#nosotros" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('footer.aboutUs')}
                  </a>
                </li>
                <li>
                  <a href="/empleo" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('footer.employment')}
                  </a>
                </li>
                <li>
                  <a href="#casos-exito" className="hover:text-white transition-colors flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    {t('footer.projects')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="text-xl font-semibold text-white mb-6">{t('footer.contactInfo')}</h4>
              <div className="space-y-6 text-gray-300">
                <div>
                  <h5 className="text-lg font-semibold text-white mb-3">{t('footer.centralOffice')}</h5>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <MapPin className="w-4 h-4 mt-1 mr-2 text-white flex-shrink-0" />
                      <div>
                        <p>Azopardo 770, Piso 15</p>
                        <p className="text-sm">Buenos Aires, Argentina</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Phone className="w-4 h-4 mt-1 mr-2 text-white flex-shrink-0" />
                       <div>
                         <p>+54 11 5263 5100</p>
                       </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h5 className="text-lg font-semibold text-white mb-3">{t('footer.spainOffice')}</h5>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <MapPin className="w-4 h-4 mt-1 mr-2 text-white flex-shrink-0" />
                      <div>
                        <p>C/ Manresa 5, Terrassa</p>
                        <p className="text-sm">Barcelona, España</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Phone className="w-4 h-4 mt-1 mr-2 text-white flex-shrink-0" />
                      <div>
                        <p>+34 930482164</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Mail className="w-5 h-5 mt-1 mr-3 text-white flex-shrink-0" />
                  <div>
                    <p className="font-medium text-white mb-1">{t('footer.email')}</p>
                    <p>operaciones@enausa-global.com</p>
                    <p>proyectos@enausa-global.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="w-5 h-5 mt-1 mr-3 text-white flex-shrink-0" />
                  <div>
                    <p className="font-medium text-white mb-1">{t('footer.hours')}</p>
                    <p>{t('footer.mondayFriday')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-enausa-navy-light/30 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-300 text-sm">
              <p className="text-gray-300">{t('footer.copyright')}</p>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <a href="#" className="hover:text-white transition-colors flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                {t('footer.privacyPolicy')}
              </a>
              <a href="#" className="hover:text-white transition-colors flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                {t('footer.termsOfService')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;